import { Badge } from "@/components/ui/badge";
import { LanguageBadge } from "../contact-language/LanguageBadge";
import { formatLanguage, languageLabel } from "../contact-language/languages";
import {
  REAL_ESTATE_FILM_LANGUAGES,
  realEstateLanguageViolation,
  type RealEstateLanguageFacts,
} from "./realEstateLanguage";
import type { EchoflicksVideoSession } from "../types";

/**
 * 🌐 marker for an Echo whose interview, title and narration ran in a
 * language other than English (plan 2026-09-21 §4.11 / LANG-501). The
 * dashboard indexer mirrors the orchestrator's `language.resolved` decision
 * onto the ECHO row as `content_language` (+ `_source`).
 *
 * Renders nothing unless `content_language` is set and is not English — an
 * absent field is an English Echo, which is every Echo made before this
 * feature and every Echo on a backend that does not project the field.
 *
 * A real-estate Echo in a language that is not a real-estate film language
 * (a B2C-only language such as Russian) also gets a red warning chip: it must
 * never happen (plan 2026-10-10 §5.9), so it is shown loudly wherever the
 * Echo appears.
 */
export const EchoLanguageBadge = ({
  record,
}: {
  record?:
    | (Pick<
        EchoflicksVideoSession,
        "content_language" | "content_language_source"
      > &
        RealEstateLanguageFacts)
    | null;
}) => (
  <>
    <LanguageBadge
      kind="content"
      code={record?.content_language}
      source={record?.content_language_source}
    />
    <RealEstateLanguageWarning record={record} />
  </>
);

const RealEstateLanguageWarning = ({
  record,
}: {
  record?: RealEstateLanguageFacts | null;
}) => {
  const code = realEstateLanguageViolation(record);
  if (!code) return null;
  const title = `Real-estate films are made only in ${REAL_ESTATE_FILM_LANGUAGES.join(
    ", ",
  )}. This Echo's content language is ${formatLanguage(
    languageLabel(code),
  )} (${code}).`;
  return (
    <Badge variant="destructive" title={title} aria-label={title}>
      ⚠ Not a real-estate language
    </Badge>
  );
};
